/**
 * 与后端 lib/sso.py 配套
 * - 登录跳转（把当前系统地址作为 loginSuccessBackUrl 交给认证中心）
 * - 回跳时从地址栏取出 ssoToken 并持久化
 * - 请求头的令牌注入、登出跳转
 * 认证中心地址由环境变量提供：.env.development / .env.production
 */

import { buildFeishuOAuthUrl, isFeishuEnv } from "./feishu";

const SSO_URL = process.env.REACT_APP_SSO_URL || '';

/** 令牌参数名，与后端 lib/sso.py 的 TOKEN_NAME 保持一致 */
const TOKEN_NAME = 'ssoToken';

/** 本地登录态的存储键。 */
const ADMIN_INFO_KEY = 'adminInfo';

/** adminInfo 内保存令牌的字段，取值与认证中心协议参数名一致 */
const TOKEN_FIELD = 'ssoToken';

/** adminInfo 内保存用户信息的字段 */
const USER_FIELD = 'user';

/** 登录成功后要跳回的页面（认证中心统一回跳到 /login，目标页由本地记录） */
const RETURN_PATH_KEY = 'ssoReturnPath';

/** 进入登录中转页的路径，也是提交给认证中心的回跳地址 */
const LOGIN_PATH = '/login';

/** 认证中心是否已配置；未配置时页面直接提示，避免静默白屏 */
export const isSsoConfigured = () => !!SSO_URL;

/** 读取整个本地登录态；无值或内容损坏时返回空对象。 内容损坏（被手工改坏）不能连累登录流程，因此这里吞掉解析异常。*/
const readLoginState = () => {
  try {
    const parsed = JSON.parse(localStorage.getItem(ADMIN_INFO_KEY));
    return parsed && typeof parsed === 'object' ? parsed : {};
  } catch (err) {
    return {};
  }
};

/** 局部更新本地登录态：读出 → 合并指定字段 → 写回。*/
const patchLoginState = (patch) => {
  localStorage.setItem(
    ADMIN_INFO_KEY,
    JSON.stringify({ ...readLoginState(), ...patch })
  );
};

/** 读取本地令牌 */
export const getSsoToken = () => readLoginState()[TOKEN_FIELD] || '';

/** 保存令牌，保留已存在的用户信息 */
export const setSsoToken = (token) => {
  if (token) {
    patchLoginState({ [TOKEN_FIELD]: token });
  }
};

/** 读取本地用户信息（认证中心返回的原始用户字典），未登录返回 null */
export const getAdminInfo = () => readLoginState()[USER_FIELD] || null;

/** 保存用户信息，保留已存在的令牌 */
export const setAdminInfo = (user) => {
  if (user) {
    patchLoginState({ [USER_FIELD]: user });
  }
};

/** 清理本地登录态 */
export const clearLoginState = () => {
  localStorage.removeItem(ADMIN_INFO_KEY);
};

/** 从地址栏解析认证中心回跳时带上的 ssoToken，认证中心登录成功后跳回 {backUrl}?ssoToken=xxx，取到后立即用 history.replaceState 换掉当前地址。*/
export const pickTokenFromUrl = () => {
  if (typeof window === 'undefined') return '';

  const params = new URLSearchParams(window.location.search);
  const token = params.get(TOKEN_NAME);
  if (!token) return '';

  params.delete(TOKEN_NAME);
  const query = params.toString();
  window.history.replaceState(
    null,
    '',
    window.location.pathname + (query ? `?${query}` : '') + window.location.hash
  );
  return token;
};

/** 记录登录成功后要回到的页面 */
export const saveReturnPath = (path) => {
  const target = path || window.location.pathname + window.location.search;
  // /login 本身不作为目标页，否则会来回跳
  if (target && target.indexOf(LOGIN_PATH) !== 0) {
    localStorage.setItem(RETURN_PATH_KEY, target);
  }
};

/** 取出并清除登录成功后要回到的页面 */
export const takeReturnPath = () => {
  const path = localStorage.getItem(RETURN_PATH_KEY) || '';
  localStorage.removeItem(RETURN_PATH_KEY);
  return path;
};

/** 提交给认证中心的回跳地址，统一落在 /login 中转页 */
export const ssoBackUrl = () => `${window.location.origin}${LOGIN_PATH}`;

/**
 * 跳转登录：飞书客户端内走飞书授权免登，其余环境走认证中心登录页。
 * @param {string} [targetPath] 登录成功后希望跳回的页面；不传则跳回首页
 */
export const redirectToSsoLogin = (targetPath) => {
  if (!isSsoConfigured()) return;
  saveReturnPath(targetPath);

  const backUrl = ssoBackUrl();

  if (isFeishuEnv()) {
    const feishuUrl = buildFeishuOAuthUrl(backUrl);
    if (feishuUrl) {
      window.location.replace(feishuUrl);
      return;
    }
  }

  window.location.replace(
    `${SSO_URL}/login?loginSuccessBackUrl=${encodeURIComponent(backUrl)}`
  );
};

/**
 * 注销后回到本系统的登录中转页。
 */
export const redirectToLocalLogin = () => {
  window.location.replace(LOGIN_PATH);
};
